/**
 * Community **Shop** — every product of every shop (the navbar “ហាង / Shop” link).
 *
 * Layout follows the community feed: **left = the shops**, centre = the product
 * grid (search + sort + category chips), **right = the product categories** to
 * filter by. Everything lives in the URL (`?shop=&category=&sort=&q=`), so a
 * filtered view can be shared. “Add to cart” puts the product into **that shop's**
 * cart — every cart line keeps its `shop_id` (`CartContext`), so products of
 * different shops can sit in the same cart.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiGrid, FiPlus, FiSearch, FiShoppingBag } from 'react-icons/fi';
import { cProducts, fullUrl } from '../../api';
import { useLanguage } from '../../i18n';
import { readShop, saveShop } from '../../utils/shopCache';
import ShopProductCard from '../../components/community/ShopProductCard';
import ShopSocialLinks from '../../components/community/ShopSocialLinks';
import { BLUE } from '../../components/community/brand';

const PAGE_SIZE = 24;
const EMPTY_DATA = { items: [], shops: [], categories: [], total: 0, pages: 1 };

const SORTS = [
  { key: 'random', label: 'randomFeed' },
  { key: 'newest', label: 'newestFirst' },
  { key: 'featured', label: 'featuredFirst' },
  { key: 'price_asc', label: 'priceLow' },
  { key: 'price_desc', label: 'priceHigh' },
];

export default function ShopPage() {
  const { t, lang } = useLanguage();
  const [params, setParams] = useSearchParams();
  const shop = params.get('shop') || '';
  const category = params.get('category') || '';
  const categoryId = params.get('category_id') || '';
  const q = params.get('q') || '';
  const sort = params.get('sort') || 'random';       // discovery: mixed by default
  // One seed per visit → the “Random” order stays stable while paging.
  const seedRef = useRef(Math.floor(Math.random() * 90000) + 1000);

  const [data, setData] = useState(EMPTY_DATA);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState(q);

  const paramsKey = `${shop}|${category}|${categoryId}|${q}|${sort}`;
  const loadedPages = useRef(new Set());   // pages already inside `items`
  const itemsRef = useRef([]);             // the list as it is right now
  const scrollY = useRef(0);               // reading position of this view

  const setParam = useCallback((key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setParams(next, { replace: true });
  }, [params, setParams]);

  // ── Coming back from a product page: restore this view, do not refetch ──────
  // Remounting used to fetch page 1 again — every loaded product was thrown
  // away, “Random” drew a new seed (order changed) and the reader jumped to the
  // top. The cache (utils/shopCache) brings the whole view back: same products,
  // same order, same page, same scroll position.
  useEffect(() => {
    const hit = readShop(paramsKey);
    if (hit && (hit.items || []).length) {
      if (hit.seed) seedRef.current = hit.seed;
      itemsRef.current = hit.items;
      loadedPages.current = new Set(Array.from({ length: hit.page || 1 }, (_, i) => i + 1));
      scrollY.current = hit.scrollY || 0;
      setData(hit.data || EMPTY_DATA);
      setItems(hit.items);
      setPage(hit.page || 1);
      setLoading(false);
      // Scroll back — but only if the browser did not restore it already.
      let timer = null;
      if ((hit.scrollY || 0) > 40) {
        timer = setTimeout(() => {
          if (window.scrollY < 40) window.scrollTo({ top: hit.scrollY });
        }, 80);
      }
      return () => { if (timer) clearTimeout(timer); };
    }
    // A filter set that was never opened → start clean on page 1.
    loadedPages.current = new Set();
    itemsRef.current = [];
    setData(EMPTY_DATA);
    setItems([]);
    setPage(1);
    setLoading(true);
    return undefined;
  }, [paramsKey]);

  // Fetch the wanted page — skipped when the cache already holds it.
  useEffect(() => {
    if (loadedPages.current.has(page)) return undefined;
    let alive = true;
    setLoading(true);
    cProducts({
      shop: shop || undefined,
      category: category || undefined,
      category_id: categoryId || undefined,
      q: q || undefined,
      sort,
      seed: sort === 'random' ? seedRef.current : undefined,
      page,
      limit: PAGE_SIZE,
    })
      .then((res) => {
        if (!alive) return;
        const rows = res?.items || [];
        const next = page === 1 ? rows : [...itemsRef.current, ...rows];
        loadedPages.current.add(page);
        itemsRef.current = next;
        setData(res || EMPTY_DATA);
        setItems(next);
        // Remember this view for “Back from a product”.
        saveShop(paramsKey, {
          items: next, page, data: res || EMPTY_DATA, seed: seedRef.current,
        });
      })
      .catch(() => {
        if (alive) { setData(EMPTY_DATA); setItems([]); itemsRef.current = []; }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [shop, category, categoryId, q, sort, page, paramsKey]);

  // Keep the reading position (flushed into the cache when this view is left).
  useEffect(() => {
    const onScroll = () => { scrollY.current = window.scrollY; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (scrollY.current > 0) saveShop(paramsKey, { scrollY: scrollY.current }, false);
    };
  }, [paramsKey]);

  useEffect(() => { setTerm(q); }, [q]);

  const activeShop = useMemo(
    () => (data.shops || []).find((s) => s.username === shop) || null,
    [data.shops, shop],
  );
  const currency = activeShop?.currency || 'USD';
  const pages = data.pages || 1;

  return (
    <div className="mx-auto max-w-[1500px] px-3 lg:px-4 py-4 grid grid-cols-1
                    lg:grid-cols-[230px_minmax(0,1fr)_230px] xl:grid-cols-[270px_minmax(0,1fr)_270px]
                    gap-4 xl:gap-5 items-start">
      {/* ── Left: every shop ───────────────────────────────────────────────── */}
      <aside className="hidden lg:block no-scrollbar space-y-3 sticky top-[72px] max-h-[calc(100vh-88px)] overflow-y-auto overscroll-contain">
        <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-3">
          <h3 className="flex items-center gap-2 font-extrabold text-sm px-2 py-1.5">
            <span className="w-6 h-6 rounded-lg flex items-center justify-center text-white text-xs" style={{ background: BLUE }}>
              <FiShoppingBag className="w-3.5 h-3.5" />
            </span>
            {t('filterByShop')}
          </h3>

          <button
            type="button"
            onClick={() => setParam('shop', '')}
            className={`w-full flex items-center justify-between px-2.5 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${
              !shop ? 'font-bold' : 'text-gray-700 dark:text-gray-200'
            }`}
            style={!shop ? { color: BLUE } : undefined}
          >
            <span>🏬 {t('allShopsLabel')}</span>
            <span className="text-[11px] text-gray-400">{data.grand_total || 0}</span>
          </button>

          <div className="mt-0.5 space-y-0.5">
            {(data.shops || []).map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setParam('shop', s.username)}
                className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-xl text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-800 ${
                  shop === s.username ? 'font-bold' : 'text-gray-700 dark:text-gray-200'
                }`}
                style={shop === s.username ? { color: BLUE } : undefined}
              >
                {s.logo ? (
                  <img src={fullUrl(s.logo)} alt={s.shop_name} className="w-8 h-8 rounded-lg object-cover shrink-0" />
                ) : (
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[10px] font-bold shrink-0" style={{ background: BLUE }}>
                    {(s.shop_name || s.username).slice(0, 2).toUpperCase()}
                  </span>
                )}
                <span className="flex-1 min-w-0">
                  <span className="block truncate font-semibold">{s.shop_name}</span>
                  <span className="block text-[11px] text-gray-400">{s.product_count} {t('productsCount')}</span>
                </span>
              </button>
            ))}
            {!loading && (data.shops || []).length === 0 && (
              <p className="px-2.5 py-2 text-xs text-gray-400">{t('loading')}</p>
            )}
          </div>

          <Link to="/shops" className="mt-2 block px-2.5 py-2 text-xs font-bold" style={{ color: BLUE }}>
            {t('shopsDirectory')} →
          </Link>

          {/* Anyone can open their own shop right from here */}
          <Link
            to="/create-shop"
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-white text-xs font-bold"
            style={{ background: BLUE }}
          >
            <FiPlus className="w-4 h-4" /> {t('createShopTitle')}
          </Link>
          <p className="mt-1 px-1 text-center text-[11px] text-gray-400">{t('createShopDesc')}</p>
        </section>
      </aside>

      {/* ── Centre: the products ───────────────────────────────────────────── */}
      <main className="space-y-4 min-w-0">
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {activeShop && (
                activeShop.logo ? (
                  <img
                    src={fullUrl(activeShop.logo)}
                    alt={activeShop.shop_name}
                    className="w-11 h-11 rounded-xl object-cover shrink-0"
                  />
                ) : (
                  <span
                    className="w-11 h-11 rounded-xl flex items-center justify-center text-white text-xs font-bold shrink-0"
                    style={{ background: BLUE }}
                  >
                    {(activeShop.shop_name || activeShop.username).slice(0, 2).toUpperCase()}
                  </span>
                )
              )}
              <div className="min-w-0">
                <h1 className="text-lg font-extrabold flex items-center gap-2">
                  <FiGrid style={{ color: BLUE }} />
                  <span className="truncate">{activeShop ? activeShop.shop_name : t('allProducts')}</span>
                </h1>
                <p className="mt-0.5 text-xs text-gray-500 truncate">
                  {activeShop
                    ? `@${activeShop.username} · ${activeShop.product_count || 0} ${t('productsCount')}`
                    : t('shopProductsDesc')}
                </p>
              </div>
            </div>

            {/* Create shop — always in the content area (the rails are hidden on phones) */}
            <div className="flex items-center gap-2 shrink-0">
              {activeShop && (
                <Link
                  to={`/${activeShop.username}`}
                  className="hidden sm:inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-xl border text-sm font-bold hover:bg-gray-50 dark:hover:bg-gray-800"
                  style={{ borderColor: BLUE, color: BLUE }}
                >
                  {t('visitShop')} →
                </Link>
              )}
              <Link
                to="/create-shop"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-white text-sm font-bold whitespace-nowrap shadow-sm"
                style={{ background: BLUE }}
              >
                <FiPlus className="w-4 h-4" /> {t('createShopTitle')}
              </Link>
            </div>
          </div>

          {/* The shop's own buttons — every social link the owner saved in the
              dashboard + the shop contact (community shop detail). */}
          {activeShop && (
            <ShopSocialLinks
              social={activeShop.social_media}
              contact={activeShop.contact}
              compact
              className="mt-3"
            />
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); setParam('q', term.trim()); }}
            className="mt-3 flex flex-wrap items-center gap-2"
          >
            <label className="flex flex-1 min-w-[170px] items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2 text-gray-500">
              <FiSearch className="w-4 h-4" />
              <input
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder={t('searchPlaceholder')}
                className="w-full bg-transparent outline-none text-sm dark:text-gray-100"
              />
            </label>

            <label className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2">
              <span className="text-xs font-semibold text-gray-500 whitespace-nowrap">{t('sortBy')}</span>
              <select
                value={sort}
                onChange={(e) => setParam('sort', e.target.value)}
                className="bg-transparent outline-none text-sm font-semibold dark:text-gray-100"
              >
                {SORTS.map((s) => <option key={s.key} value={s.key}>{t(s.label)}</option>)}
              </select>
            </label>

            <button type="submit" className="px-4 py-2 rounded-xl text-white text-sm font-bold" style={{ background: BLUE }}>
              {t('search')}
            </button>
          </form>

          {/* Category filter — the same list is in the right rail */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setParam('category', '')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold border ${
                !category && !categoryId
                  ? 'text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700'
              }`}
              style={!category && !categoryId ? { background: BLUE, borderColor: BLUE } : undefined}
            >
              {t('allCategories')}
            </button>
            {(data.categories || []).slice(0, 12).map((c) => {
              const on = category.toLowerCase() === c.name.toLowerCase();
              return (
                <button
                  key={`chip-${c.id || 'name'}-${c.name}`}
                  type="button"
                  onClick={() => setParam('category', c.name)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold border ${
                    on ? 'text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                  }`}
                  style={on ? { background: BLUE, borderColor: BLUE } : undefined}
                >
                  {c.name} · {c.count}
                </button>
              );
            })}
          </div>
        </div>

        {items.length === 0 ? (
          <p className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 p-10 text-center text-sm text-gray-400">
            {loading ? t('loading') : t('noProducts')}
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {items.map((p) => <ShopProductCard key={p.id} product={p} />)}
          </div>
        )}

        {page < pages && (
          <div className="text-center">
            <button
              type="button"
              onClick={() => setPage((v) => v + 1)}
              disabled={loading}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-60"
              style={{ background: BLUE }}
            >
              {loading ? t('loading') : `${t('seeAll')} (${data.total || 0})`}
            </button>
          </div>
        )}
      </main>

      {/* ── Right: filter by product category ──────────────────────────────── */}
      <aside className="hidden lg:block no-scrollbar space-y-4 sticky top-[72px] max-h-[calc(100vh-88px)] overflow-y-auto overscroll-contain">
        <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
          <h3 className="font-extrabold text-sm mb-2 flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg flex items-center justify-center text-white text-xs" style={{ background: BLUE }}>#</span>
            {t('filterByCategory')}
          </h3>
          <div className="space-y-0.5">
            <button
              type="button"
              onClick={() => setParam('category', '')}
              className={`w-full flex items-center justify-between px-2.5 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${
                !category && !categoryId ? 'font-bold' : 'text-gray-700 dark:text-gray-200'
              }`}
              style={!category && !categoryId ? { color: BLUE } : undefined}
            >
              <span>🌈 {t('allCategories')}</span>
              <span className="text-[11px] text-gray-400">{data.grand_total || 0}</span>
            </button>
            {(data.categories || []).map((c) => {
              const on = category.toLowerCase() === c.name.toLowerCase();
              return (
                <button
                  key={`rail-${c.id || 'name'}-${c.name}`}
                  type="button"
                  onClick={() => setParam('category', c.name)}
                  className={`w-full flex items-center justify-between px-2.5 py-2 rounded-xl text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${
                    on ? 'font-bold' : 'text-gray-700 dark:text-gray-200'
                  }`}
                  style={on ? { color: BLUE } : undefined}
                >
                  <span className="truncate">{c.name}</span>
                  <span className="text-[11px] text-gray-400 shrink-0">{c.count}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 px-2.5 text-[11px] text-gray-400">
            {lang === 'kh' ? 'តម្លៃជា' : 'Prices in'} {currency}
          </p>
        </section>
      </aside>
    </div>
  );
}
